import { useEffect, useMemo, useState } from "react";
import { Building2, CalendarCheck, HandCoins, Layers, Loader2, Pencil, Search } from "lucide-react";
import { useAuth } from "@/App";
import { usePageHeader } from "@/components/layouts/lib/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserKpiCard } from "@/pages/users/components/UserKpiCard";
import {
  useCompanyCommissions,
  useCompanyCommissionBatches,
  useCompanyCommissionStats,
  useCompanyCommissionSupplierTotals,
  type CompanyCommissionStatusFilter,
} from "@/hooks/queries/useCompanyCommissions";
import { useCompanyCommissionSuppliers } from "@/hooks/queries/useCompanyCommissionSuppliers";
import {
  useConsolidateCompanyCommissionsMutation,
  useUpdateCompanyCommissionMutation,
} from "@/hooks/mutations/useCompanyCommissionMutations";
import type { CompanyCommission, CompanyCommissionBatch } from "@/lib/company-commission.api";

const TABS: { value: CompanyCommissionStatusFilter; label: string }[] = [
  { value: "pending", label: "À récupérer" },
  { value: "consolidated", label: "Consolidées" },
  { value: "voided", label: "Annulées" },
  { value: "all", label: "Toutes" },
];

const STATUS_LABEL: Record<CompanyCommission["status"], string> = {
  pending: "À récupérer",
  consolidated: "Consolidée",
  voided: "Annulée",
};

function fmtXaf(v: string | number): string {
  return `${Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} XAF`;
}
function fmtDate(v?: string): string {
  return v ? new Date(v).toLocaleDateString("fr-FR", { dateStyle: "short" }) : "—";
}

// Consolidation history: one supplier -> its name; several -> "N fournisseurs".
function batchSupplierLabel(names: string[]): string {
  if (names.length === 0) return "—";
  if (names.length === 1) return names[0];
  return `${names.length} fournisseurs`;
}

export default function CompanyCommissionsPage() {
  const { user } = useAuth();
  const canConsolidate = Boolean(user && ["admin", "super_admin"].includes(user.role));

  const [tab, setTab] = useState<CompanyCommissionStatusFilter>("pending");
  const [supplierId, setSupplierId] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState<CompanyCommission | null>(null);
  const [viewingBatch, setViewingBatch] = useState<CompanyCommissionBatch | null>(null);

  const { data: allRows = [], isLoading } = useCompanyCommissions(tab, supplierId);
  const { data: suppliers = [] } = useCompanyCommissionSuppliers();
  const { data: allBatches = [] } = useCompanyCommissionBatches();
  const { data: stats } = useCompanyCommissionStats();
  const { data: supplierTotals = [] } = useCompanyCommissionSupplierTotals();
  const consolidateMutation = useConsolidateCompanyCommissionsMutation();

  usePageHeader({ title: "Commissions compagnies" });

  // Free-text search over the fields that identify a row for a user: supplier,
  // invoice number/id, passenger, status label, amount. Applied client-side on
  // top of the tab + supplier-dropdown server filters.
  const q = search.trim().toLowerCase();
  const rows = useMemo(() => {
    if (!q) return allRows;
    return allRows.filter((r) =>
      [
        r.supplierNameSnapshot,
        r.invoiceNumber ?? "",
        `#${r.invoiceId}`,
        r.passengerName ?? "",
        STATUS_LABEL[r.status],
        r.amount,
        Number(r.amount).toLocaleString("fr-FR"),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [allRows, q]);
  const batches = useMemo(() => {
    if (!q) return allBatches;
    return allBatches.filter((b) =>
      [
        `#${b.id}`,
        b.note ?? "",
        b.supplierNames.join(" "),
        b.totalAmount,
        Number(b.totalAmount).toLocaleString("fr-FR"),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [allBatches, q]);

  const selectableIds = useMemo(
    () => rows.filter((r) => r.status === "pending").map((r) => r.id),
    [rows],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const selectedTotal = selectedRows.reduce((s, r) => s + Number(r.amount), 0);

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  }
  function resetSelection() {
    setSelected(new Set());
    setNote("");
  }
  function handleConsolidate() {
    consolidateMutation.mutate(
      { commissionIds: [...selected], note: note.trim() || undefined },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          resetSelection();
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <UserKpiCard
          label="À récupérer"
          value={stats ? fmtXaf(stats.pendingAmount) : "—"}
          detail={stats ? `${stats.pendingCount} commission${stats.pendingCount > 1 ? "s" : ""}` : "Chargement…"}
          icon={HandCoins}
          tone="gold"
        />
        <UserKpiCard
          label="Consolidé ce mois"
          value={stats ? fmtXaf(stats.consolidatedThisMonthAmount) : "—"}
          detail={
            stats
              ? `${stats.consolidatedThisMonthCount} ligne${stats.consolidatedThisMonthCount > 1 ? "s" : ""}`
              : "Chargement…"
          }
          icon={CalendarCheck}
          tone="success"
        />
        <UserKpiCard
          label="Fournisseurs actifs"
          value={stats ? String(stats.activeSupplierCount) : "—"}
          detail={stats ? `sur ${stats.totalSupplierCount} au total` : "Chargement…"}
          icon={Building2}
          tone="neutral"
        />
        <UserKpiCard
          label="Volume à traiter"
          value={stats ? String(stats.pendingCount) : "—"}
          detail="ligne(s) en attente de consolidation"
          icon={Layers}
          tone={stats && stats.pendingCount > 0 ? "gold" : "neutral"}
        />
      </div>

      {supplierTotals.length > 0 && (
        <div>
          <h2 className="mb-2 text-[13px] font-semibold text-foreground">Totaux par fournisseur</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-[12px]">
              <thead className="bg-surface-muted text-[10.5px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Fournisseur</th>
                  <th className="px-3 py-2 text-right">En attente</th>
                  <th className="px-3 py-2 text-right">Lignes en attente</th>
                  <th className="px-3 py-2 text-right">Consolidé (historique)</th>
                  <th className="px-3 py-2">Dernière commission</th>
                </tr>
              </thead>
              <tbody>
                {supplierTotals.map((s) => (
                  <tr key={s.supplierId} className="border-t border-border">
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="text-body hover:text-brand-gold-dark hover:underline"
                        onClick={() => setSupplierId(s.supplierId)}
                        title="Filtrer la liste sur ce fournisseur"
                      >
                        {s.supplierName || `#${s.supplierId}`}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">
                      {fmtXaf(s.pendingAmount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.pendingCount}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {fmtXaf(s.consolidatedAmount)}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{fmtDate(s.lastCommissionAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => {
              setTab(t.value);
              resetSelection();
            }}
            className={`rounded-full border px-3 py-1 text-[12px] font-medium ${
              tab === t.value
                ? "border-brand-gold-dark bg-brand-gold-dark text-white"
                : "border-border bg-card text-muted-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}

        <div className="relative ml-auto w-full max-w-xs sm:w-64">
          <Search
            size={13}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (fournisseur, facture, passager…)"
            className="h-8 pl-8 text-[12px]"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-muted-foreground">Fournisseur</Label>
          <Select
            value={supplierId ? String(supplierId) : "all"}
            onValueChange={(v) => {
              setSupplierId(v === "all" ? undefined : Number(v));
              resetSelection();
            }}
          >
            <SelectTrigger className="h-8 w-52 text-[12px]">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les fournisseurs</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {tab === "pending" && canConsolidate && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-surface-muted px-4 py-2.5">
          <p className="text-[12px] text-muted-foreground">
            {selected.size} sélectionnée(s) — {fmtXaf(selectedTotal)}
          </p>
          <Button size="sm" disabled={selected.size === 0} onClick={() => setConfirmOpen(true)}>
            Consolider la sélection
          </Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-[12px]">
          <thead className="bg-surface-muted text-[10.5px] uppercase tracking-wide text-muted-foreground">
            <tr>
              {tab === "pending" && canConsolidate && (
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Tout sélectionner"
                  />
                </th>
              )}
              <th className="px-3 py-2">Fournisseur</th>
              <th className="px-3 py-2 text-right">Prix HT</th>
              <th className="px-3 py-2 text-right">%</th>
              <th className="px-3 py-2 text-right">Montant</th>
              <th className="px-3 py-2">Facture</th>
              <th className="px-3 py-2">Activée</th>
              <th className="px-3 py-2">Statut</th>
              {canConsolidate && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center">
                  <Loader2 className="mx-auto animate-spin text-subtle" size={16} />
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">
                  Aucune commission.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border">
                {tab === "pending" && canConsolidate && (
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                      disabled={r.status !== "pending"}
                      aria-label={`Sélectionner ${r.id}`}
                    />
                  </td>
                )}
                <td className="px-3 py-2 text-body">
                  {r.supplierNameSnapshot}
                  {r.passengerName && (
                    <span className="block text-[10.5px] text-subtle">{r.passengerName}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtXaf(r.priceHt)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{Number(r.commissionPct)}</td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">{fmtXaf(r.amount)}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.invoiceNumber ?? `#${r.invoiceId}`}</td>
                <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.activatedAt)}</td>
                <td className="px-3 py-2">
                  <span className="text-[11px] text-muted-foreground">{STATUS_LABEL[r.status]}</span>
                  {r.status === "consolidated" && r.consolidatedAt && (
                    <span className="ml-1 text-[10.5px] text-subtle">({fmtDate(r.consolidatedAt)})</span>
                  )}
                </td>
                {canConsolidate && (
                  <td className="px-3 py-2 text-right">
                    {r.status === "pending" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Corriger"
                        onClick={() => setEditing(r)}
                      >
                        <Pencil size={13} />
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h2 className="mb-2 text-[13px] font-semibold text-foreground">Historique des consolidations</h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-[12px]">
            <thead className="bg-surface-muted text-[10.5px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Fournisseur(s)</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-5 text-center text-muted-foreground">
                    Aucune consolidation.
                  </td>
                </tr>
              )}
              {batches.map((b) => (
                <tr key={b.id} className="border-t border-border">
                  <td className="px-3 py-2 text-muted-foreground">#{b.id}</td>
                  <td className="px-3 py-2">{fmtDate(b.consolidatedAt)}</td>
                  <td className="px-3 py-2">
                    {b.supplierNames.length > 1 ? (
                      <button
                        type="button"
                        className="text-brand-gold-dark hover:underline"
                        onClick={() => setViewingBatch(b)}
                        title={b.supplierNames.join(", ")}
                      >
                        {batchSupplierLabel(b.supplierNames)}
                      </button>
                    ) : (
                      <span className="text-body">{batchSupplierLabel(b.supplierNames)}</span>
                    )}
                    <span className="ml-1 text-[10.5px] text-subtle">
                      · {b.count} ligne{b.count > 1 ? "s" : ""}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">{fmtXaf(b.totalAmount)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{b.note ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Consolider {selected.size} commission(s) ?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[12px]">
            <p className="text-muted-foreground">
              Total : <span className="font-semibold text-foreground">{fmtXaf(selectedTotal)}</span>.
              Les lignes sélectionnées passeront en « Consolidée » et compteront dans le CA / Gain à
              la date d'aujourd'hui. Action non réversible ici.
            </p>
            <div>
              <Label htmlFor="cc-note">Note (optionnelle)</Label>
              <textarea
                id="cc-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="flex w-full rounded border border-border bg-card px-3 py-2 text-sm resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={consolidateMutation.isPending}>
              Annuler
            </Button>
            <Button onClick={handleConsolidate} disabled={consolidateMutation.isPending}>
              {consolidateMutation.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                "Consolider"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CorrectDialog commission={editing} onClose={() => setEditing(null)} />

      <Dialog open={Boolean(viewingBatch)} onOpenChange={(o) => !o && setViewingBatch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Consolidation #{viewingBatch?.id}</DialogTitle>
          </DialogHeader>
          {viewingBatch && (
            <div className="space-y-3 text-[12px]">
              <div className="grid grid-cols-3 gap-3 text-muted-foreground">
                <div>
                  <p className="text-[10.5px] uppercase tracking-wide">Date</p>
                  <p className="text-body">{fmtDate(viewingBatch.consolidatedAt)}</p>
                </div>
                <div>
                  <p className="text-[10.5px] uppercase tracking-wide">Lignes</p>
                  <p className="text-body">{viewingBatch.count}</p>
                </div>
                <div>
                  <p className="text-[10.5px] uppercase tracking-wide">Total</p>
                  <p className="text-body">{fmtXaf(viewingBatch.totalAmount)}</p>
                </div>
              </div>
              <div>
                <p className="mb-1 text-[10.5px] uppercase tracking-wide text-muted-foreground">
                  Fournisseurs ({viewingBatch.supplierNames.length})
                </p>
                <ul className="list-inside list-disc text-body">
                  {viewingBatch.supplierNames.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>
              {viewingBatch.note && (
                <div>
                  <p className="mb-1 text-[10.5px] uppercase tracking-wide text-muted-foreground">Note</p>
                  <p className="text-body">{viewingBatch.note}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setViewingBatch(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CorrectDialog({
  commission,
  onClose,
}: {
  commission: CompanyCommission | null;
  onClose: () => void;
}) {
  const updateMutation = useUpdateCompanyCommissionMutation();
  const [supplierName, setSupplierName] = useState("");
  const [priceHt, setPriceHt] = useState(0);
  const [commissionPct, setCommissionPct] = useState(0);

  // seed the form each time a different row opens
  useEffect(() => {
    if (commission) {
      setSupplierName(commission.supplierNameSnapshot);
      setPriceHt(Number(commission.priceHt));
      setCommissionPct(Number(commission.commissionPct));
    }
  }, [commission?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function submit() {
    if (!commission) return;
    updateMutation.mutate(
      { id: commission.id, patch: { supplierName, priceHt, commissionPct } },
      { onSuccess: onClose },
    );
  }

  const estimate = (Number(priceHt) || 0) * (Number(commissionPct) || 0) / 100;

  return (
    <Dialog open={Boolean(commission)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Corriger la commission #{commission?.id}</DialogTitle>
        </DialogHeader>
        {commission && (
          <div className="space-y-3">
            <div>
              <Label>Agence / Fournisseur</Label>
              <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prix HT</Label>
                <Input type="number" min={0} step="0.01" value={priceHt} onChange={(e) => setPriceHt(parseFloat(e.target.value) || 0)} />
              </div>
              <div>
                <Label>%</Label>
                <Input type="number" min={0} step="0.001" value={commissionPct} onChange={(e) => setCommissionPct(parseFloat(e.target.value) || 0)} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Nouveau montant : <span className="font-semibold text-foreground">{fmtXaf(estimate)}</span>
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={updateMutation.isPending}>
            Annuler
          </Button>
          <Button onClick={submit} disabled={updateMutation.isPending || !supplierName.trim()}>
            {updateMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
